import React, { useEffect, useState } from 'react';
import { invoicesService } from '../../services/api';
import { A_PROPRIA_LISTA_DIZ_O_VAZIO, EstadoDaLista, useEstadoDaCarga, textos } from '../../design';
import { FAIXAS_DE_ATRASO, FaixaDeAtraso, ResumoDaFila, resumoDaFilaValido } from '../../utils/filaDeCobranca';

/**
 * INADIMPLÊNCIA POR FAIXA no Dashboard (decisão X1 do Heitor, 23/10/2026; faixas e fonte trocadas na A2/K1 de 02/11).
 *
 * O vencido EM ABERTO da carteira inteira, por faixa de atraso. Desde A2/K1 lê o `aging` do resumo da fila de cobrança
 * (`GET /invoices/dunning-candidates/summary`), o MESMO que a barra de faixas da Cobrança usa: uma régua só no HUB
 * (1-30, 31-60, 61-90, 90+), e a régua é do servidor. Antes eram as faixas do resumo de recebíveis (1-15, 16-30, 31-60,
 * 60+), que a Cobrança não tinha. Cada faixa abre a Cobrança já filtrada nela. O saldo é o que falta pagar.
 */

const fmt = (v: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function InadimplenciaPorFaixa({ onAbrir }: {
  /** Abre a Cobrança na faixa dada; `null` = todas. */
  onAbrir: (faixa: FaixaDeAtraso | null) => void;
}) {
  const { rodar, ...carga } = useEstadoDaCarga();
  const [resumo, setResumo] = useState<ResumoDaFila | null>(null);

  const ler = () => rodar(async () => {
    const r = resumoDaFilaValido(await invoicesService.dunningQueueSummary());
    if (!r) throw new Error('O resumo da fila de cobrança veio fora da forma esperada.');
    setResumo(r);
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { ler(); }, []);

  const faixas = FAIXAS_DE_ATRASO.map((f) => ({ ...f, count: resumo?.aging[f.chave]?.count ?? 0, value: resumo?.aging[f.chave]?.value ?? 0 }));
  const qtd = resumo?.vencidas.count ?? 0;
  const maior = Math.max(1, ...faixas.map((f) => f.value));

  return (
    <div className='bg-central-cartao rounded-central border border-central-borda overflow-hidden' data-cartao='inadimplencia'>
      <div className='p-4 border-b border-central-borda flex items-center justify-between gap-3'>
        <div>
          <h3 className='text-central-texto font-semibold'>Inadimplência</h3>
          <p className='text-central-texto-fraco text-sm'>Vencido em aberto, toda a carteira, por atraso</p>
        </div>
        <div className='flex items-center gap-3'>
          <div className='text-right'>
            <p className='text-2xl font-bold text-central-texto tabular-nums' data-inadimplencia='total'>{resumo ? `R$ ${fmt(resumo.vencidas.value)}` : textos.numeroSemFonte}</p>
            {resumo && <p className='text-central-texto-fraco text-xs'>{qtd} {qtd === 1 ? 'fatura' : 'faturas'}</p>}
          </div>
          <button type='button' onClick={() => onAbrir(null)} className='px-3 py-1.5 text-sm bg-central-cartao border border-central-borda text-central-texto hover:bg-central-elevado rounded-lg whitespace-nowrap'>Abrir Cobrança</button>
        </div>
      </div>
      <div className='p-4'>
        <EstadoDaLista carga={carga} temDados={!!resumo} oQue='o vencido em aberto' onTentarDeNovo={ler} vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}>
          {qtd === 0 ? (
            <p className='text-central-texto-fraco text-sm' data-inadimplencia='vazio'>Nenhuma fatura vencida em aberto.</p>
          ) : (
            <div className='space-y-1'>
              {faixas.map((f) => (
                <button
                  key={f.chave}
                  type='button'
                  disabled={f.count === 0}
                  onClick={() => onAbrir(f.chave)}
                  title={f.count ? `Abrir a Cobrança com ${f.extenso} de atraso` : undefined}
                  className='w-full grid grid-cols-[8rem_1fr_auto] items-center gap-3 px-2 py-1.5 -mx-2 rounded-lg text-left hover:bg-central-elevado disabled:hover:bg-transparent disabled:cursor-default'
                  data-faixa={f.chave}
                >
                  <span className={`text-sm ${f.count ? 'text-central-texto underline decoration-central-borda underline-offset-2' : 'text-central-texto-fraco'}`}>{f.extenso}</span>
                  <div className='h-2 rounded-full bg-central-elevado overflow-hidden'>
                    <div className='h-2 rounded-full bg-slate-500' style={{ width: `${(f.value / maior) * 100}%` }} />
                  </div>
                  <span className='text-sm text-central-texto tabular-nums whitespace-nowrap'>
                    R$ {fmt(f.value)} <span className='text-central-texto-fraco'>· {f.count} fat.</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </EstadoDaLista>
      </div>
    </div>
  );
}
